/**
 * Monday-through-Sunday Eastern Time week ranges, expressed as full ISO
 * instants. ET midnight is resolved per-date against America/New_York, so the
 * spring-forward week is 167 hours and the fall-back week 169 — a fixed UTC
 * offset would drift both boundaries by an hour for half the year.
 */

export interface WeekRange {
  from: string;
  to: string;
  weekStartDate: string;
}

interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

const ET_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  hourCycle: 'h23',
});

function etCalendarDateOf(instant: Date): CalendarDate {
  const parts: Record<string, string> = {};
  for (const part of ET_PARTS.formatToParts(instant)) parts[part.type] = part.value;
  return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day) };
}

/** The UTC instant at which the given ET calendar date reaches 00:00:00.000. */
function etMidnightInstant(date: CalendarDate): Date {
  // ET is UTC-5 (EST) or UTC-4 (EDT); DST transitions happen at 2am, so local
  // midnight always exists and exactly one candidate maps back to it.
  for (const offsetHours of [5, 4]) {
    const candidate = new Date(Date.UTC(date.year, date.month - 1, date.day, offsetHours));
    const parts: Record<string, string> = {};
    for (const part of ET_PARTS.formatToParts(candidate)) parts[part.type] = part.value;
    if (
      Number(parts.year) === date.year &&
      Number(parts.month) === date.month &&
      Number(parts.day) === date.day &&
      Number(parts.hour) === 0
    ) {
      return candidate;
    }
  }
  throw new Error(`No Eastern Time midnight found for ${formatCalendarDate(date)}`);
}

function addDays(date: CalendarDate, days: number): CalendarDate {
  const shifted = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

function mondayOf(date: CalendarDate): CalendarDate {
  const weekday = new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay();
  return addDays(date, -((weekday + 6) % 7));
}

function formatCalendarDate(date: CalendarDate): string {
  const month = String(date.month).padStart(2, '0');
  const day = String(date.day).padStart(2, '0');
  return `${date.year}-${month}-${day}`;
}

function weekRangeFromMonday(monday: CalendarDate): WeekRange {
  const nextMonday = addDays(monday, 7);
  return {
    from: etMidnightInstant(monday).toISOString(),
    to: new Date(etMidnightInstant(nextMonday).getTime() - 1).toISOString(),
    weekStartDate: formatCalendarDate(monday),
  };
}

export function weekRangeFor(now: Date = new Date()): WeekRange {
  return weekRangeFromMonday(mondayOf(etCalendarDateOf(now)));
}

export function currentWeekRange(now: Date = new Date()): WeekRange {
  return weekRangeFor(now);
}

/** Step whole weeks, clamped so navigation can never pass the current week. */
export function addWeeks(range: WeekRange, delta: number, now: Date = new Date()): WeekRange {
  const monday = parseCalendarDate(range.weekStartDate);
  const candidate = weekRangeFromMonday(addDays(monday, delta * 7));
  const current = currentWeekRange(now);
  return candidate.from > current.from ? current : candidate;
}

/** The ET week containing a bare YYYY-MM-DD calendar date. */
export function weekRangeContaining(isoDate: string): WeekRange {
  return weekRangeFromMonday(mondayOf(parseCalendarDate(isoDate)));
}

function parseCalendarDate(isoDate: string): CalendarDate {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) throw new Error(`Expected a YYYY-MM-DD date, got "${isoDate}"`);
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}
